
import { Box, Dialog, DialogContent, Typography } from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import ComboBox from "components/ComboBox"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import PaperComponent from "components/PaperComponent"
import { DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { addIdForDataGrid, dateFormat, numberFormat } from "utils"
import { tyreColNm, tyreTransactionColNm, trnsTypNm } from "./constant"
import SearchButton from "components/buttons/SearchButton"
import ClearButton from "components/buttons/ClearButton"
import dayjs from "dayjs"
import DateTimeSelect from "components/DateTimeSelect"

const initFilter = {
  TrnsDteSt: dayjs().startOf('month').format("YYYY-MM-DD 00:00:00"),
  TrnsDteEn: dayjs().endOf('month').format("YYYY-MM-DD 23:59:59"),
  TukId: null,
  TlrId: null,
  SupId: null,
}

const columns = [
  {
    field: 'TrnsDte', headerName: tyreTransactionColNm.TrnsDte, width: 105,
    valueFormatter: ({ value }) => dateFormat(value),
  },
  {
    field: 'TrnsTyp', headerName: tyreTransactionColNm.TrnsTyp, width: 155,
    valueFormatter: ({ value }) => trnsTypNm[value] || value,
  },
  { field: 'TyrCd', headerName: tyreColNm.TyrCd, width: 120 },
  { field: 'TyrBrand', headerName: tyreColNm.TyrBrand, width: 110 },
  { field: 'TyrSzNm', headerName: tyreColNm.TyrSzId, width: 110 },
  { field: 'TukCd', headerName: tyreTransactionColNm.TukId, width: 90 },
  { field: 'TlrLicNm', headerName: tyreTransactionColNm.TlrId, width: 100 },
  { field: 'TyrPos', headerName: tyreTransactionColNm.TyrPos, width: 80, align: 'center' },
  {
    field: 'Mileage', headerName: tyreTransactionColNm.Mileage, width: 115, align: 'right',
    valueFormatter: ({ value }) => value ? numberFormat(value) : '-',
  },
  {
    field: 'DistKm', headerName: 'ระยะที่ใช้ (กม.)', width: 120, align: 'right',
    valueFormatter: ({ value }) => value ? numberFormat(value) : '-',
  },
  {
    field: 'CycleDistKm', headerName: 'ระยะรอบดอก (กม.)', width: 130, align: 'right',
    valueFormatter: ({ value }) => value ? numberFormat(value) : '-',
  },
  {
    field: 'CycleCost', headerName: 'ต้นทุนรอบดอก', width: 120, align: 'right',
    valueFormatter: ({ value }) => value ? numberFormat(value) : '-',
  },
  {
    field: 'BahtPerKm', headerName: 'บาท/กม.', width: 95, align: 'right',
    valueFormatter: ({ value }) => value ? numberFormat(value) : '-',
  },
  { field: 'SupSNm', headerName: tyreColNm.SupId, width: 130 },
  { field: 'Dscp', headerName: tyreColNm.Dscp, flex: 1, minWidth: 120 },
]

const TyreUsageReportDialog = ({ dialogOpen, setDialogOpen }) => {
  const { ax, msData } = useContext(GlobalStateContext)

  const [filterData, setFilterData] = useState({ ...initFilter })
  const [dataTable, setDataTable] = useState([])

  const getData = useCallback((filter) => {
    ax.post("/tyre/getTyreUsageReport", filter).then(value => {
      if (value.data) setDataTable(addIdForDataGrid(value.data, 'TyrTrnsId'))
    })
  }, [ax])

  useEffect(() => {
    if (dialogOpen) {
      getData({ ...initFilter })
    } else {
      setFilterData({ ...initFilter })
      setDataTable([])
    }
  }, [dialogOpen, getData])

  return (
    <Dialog
      open={dialogOpen}
      onClose={(e, reason) => { if (reason === "backdropClick") return; setDialogOpen(false) }}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='xl'
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)} title="รายงานการใช้ยาง" />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 1, height: 620 }}>
          <BoxFR sx={{ gap: 1 }}>
            <DateTimeSelect
              label="วันที่"
              startDate={filterData.TrnsDteSt}
              endDate={filterData.TrnsDteEn}
              onStartDateChange={(v) => setFilterData(o => ({ ...o, TrnsDteSt: v }))}
              onEndDateChange={(v) => setFilterData(o => ({ ...o, TrnsDteEn: v }))}
            />
            <ComboBox
              options={msData.truckCombo || []}
              label={tyreColNm.TukId}
              selectedId={filterData.TukId}
              setSelectedId={(id) => setFilterData(o => ({ ...o, TukId: id }))}
              sx={{ width: 160 }}
            />
            <ComboBox
              options={msData.trailerCombo || []}
              label={tyreColNm.TlrId}
              selectedId={filterData.TlrId}
              setSelectedId={(id) => setFilterData(o => ({ ...o, TlrId: id }))}
              sx={{ width: 160 }}
            />
            <ComboBox
              options={msData.supplierCombo || []}
              label={tyreColNm.SupId}
              selectedId={filterData.SupId}
              setSelectedId={(id) => setFilterData(o => ({ ...o, SupId: id }))}
              sx={{ width: 180 }}
            />
            <SearchButton onClick={() => getData(filterData)} />
            <ClearButton onClick={() => setFilterData({ ...initFilter })} />
            <Box flex={1} />
            <Typography variant="body2" color="text.secondary">
              {dataTable.length} รายการ
            </Typography>
          </BoxFR>
          <Box flex={1} overflow='hidden'>
            <DataGridCellExpand
              hideFooter
              rows={dataTable}
              columns={columns}
            />
          </Box>
        </BoxFC>
      </DialogContent>
    </Dialog>
  )
}

export default React.memo(TyreUsageReportDialog)
